/**
 * Reviews V2 — UI controller
 *
 * Drives the PDP dropdown and the reviews page: accordion, AI summary toggle,
 * sort, pagination, and the write-review modal.
 * Depends on reviews-v2.js (window.ReviewsV2).
 */
(function() {
  'use strict';

  // ═════════════════════════════════════════════════════════════════════════
  // INIT GUARD
  // ═════════════════════════════════════════════════════════════════════════
  if (window.__reviewsV2UIInit) return;
  window.__reviewsV2UIInit = true;

  function init() {
    // PDP dropdown → .rv2-block (first page server-rendered from metafields)
    // Reviews page → #rvp2-widget (rendered from the embedded data)
    var pdp = document.querySelector('.rv2-block');
    var page = document.getElementById('rvp2-widget');
    var host = pdp || page;
    if (!host) return;

    var productId = host.dataset.productId;
    if (!productId) return;

    // pageSize on the PDP must match the `limit: 3` used by the Liquid SSR,
    // otherwise page 2 would skip reviews.
    var els = pdp
      ? { list: 'rv2-list',  pagination: 'rv2-pagination',  sort: 'rv2-sort',  writeBtn: 'rv2-write-btn',  pageSize: 3 }
      : { list: 'rvp2-list', pagination: 'rvp2-pagination', sort: 'rvp2-sort', writeBtn: 'rvp2-write-btn', pageSize: 10 };

    portalModalToBody();
    initForm(productId, els.writeBtn);
    initLightbox();

    allReviews = loadReviewData();
    initSort(els);

    if (pdp) {
      initAccordion(pdp);
      initAISummary(pdp);
      initExpandBtn(pdp);
    }

    // Render on both surfaces so the visible cards always match the active
    // sort pill, rather than trusting the metafield's own ordering.
    render(els);
  }

  // ═════════════════════════════════════════════════════════════════════════
  // PORTAL — escape ancestor stacking contexts
  // ═════════════════════════════════════════════════════════════════════════

  /**
   * Move the form overlay to <body>.
   *
   * On the PDP the overlay sits inside product-details, whose ancestors create
   * stacking contexts — a position:fixed child gets clipped there and later
   * sections paint over it regardless of z-index.
   */
  function portalModalToBody() {
    var overlay = document.getElementById('rv2-form-overlay');
    if (overlay && overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
    }
  }

  // ═════════════════════════════════════════════════════════════════════════
  // ACCORDION
  // ═════════════════════════════════════════════════════════════════════════

  function initAccordion(block) {
    var btn = block.querySelector('.rv2-btn');
    var panel = block.querySelector('.rv2-panel');
    if (!btn || !panel) return;

    btn.addEventListener('click', function() {
      var isActive = btn.getAttribute('data-active') === 'true';
      var next = isActive ? 'false' : 'true';
      btn.setAttribute('data-active', next);
      btn.setAttribute('aria-expanded', next);
      panel.setAttribute('data-active', next);
    });
  }

  function initExpandBtn(block) {
    var expandBtns = block.querySelectorAll('[data-rv2-expand]');
    expandBtns.forEach(function(btn) {
      btn.addEventListener('click', function() {
        var accBtn = block.querySelector('.rv2-btn');
        if (accBtn && accBtn.getAttribute('data-active') !== 'true') {
          accBtn.click();
        }
      });
    });
  }

  // ═════════════════════════════════════════════════════════════════════════
  // AI SUMMARY — Read More/Less
  // ═════════════════════════════════════════════════════════════════════════

  function initAISummary(block) {
    var textEl = block.querySelector('.js-rv2-ai-text');
    var toggleBtn = block.querySelector('.js-rv2-ai-toggle');
    if (!textEl || !toggleBtn) return;

    function checkOverflow() {
      if (!textEl.classList.contains('is-expanded')) {
        if (textEl.scrollHeight > textEl.clientHeight + 2) {
          toggleBtn.style.display = 'inline-block';
        } else {
          toggleBtn.style.display = 'none';
        }
      }
    }

    toggleBtn.addEventListener('click', function() {
      textEl.classList.toggle('is-expanded');
      toggleBtn.textContent = textEl.classList.contains('is-expanded') ? 'Read less' : 'Read more';
    });

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
  }

  // ═════════════════════════════════════════════════════════════════════════
  // SORT + PAGINATION (client-side, over the embedded metafield data)
  // ═════════════════════════════════════════════════════════════════════════

  var currentSort = 'newest';
  var currentPage = 0;
  var allReviews = [];

  /** Read the reviews embedded by includ-reviews-data-v2.liquid. */
  function loadReviewData() {
    var el = document.getElementById('rv2-review-data');
    if (!el) return [];
    try {
      var parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function sortReviews(reviews, sort) {
    var copy = reviews.slice();
    if (sort === 'highest') {
      copy.sort(function(a, b) { return (b.rating || 0) - (a.rating || 0); });
    } else if (sort === 'lowest') {
      copy.sort(function(a, b) { return (a.rating || 0) - (b.rating || 0); });
    } else {
      copy.sort(function(a, b) {
        return (parseInt(b.sortDate, 10) || 0) - (parseInt(a.sortDate, 10) || 0);
      });
    }
    return copy;
  }

  function initSort(els) {
    var sortContainer = document.getElementById(els.sort);
    if (!sortContainer) return;

    sortContainer.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-rv2-sort]');
      if (!btn) return;

      var sort = btn.dataset.rv2Sort;
      if (sort === currentSort) return;

      currentSort = sort;
      currentPage = 0;

      sortContainer.querySelectorAll('.rv2-sort__btn').forEach(function(b) {
        b.classList.remove('is-active');
      });
      btn.classList.add('is-active');

      render(els);
    });
  }

  /** Render the current sort + page from data already on the page. */
  function render(els) {
    var listEl = document.getElementById(els.list);
    if (!listEl) return;

    if (!allReviews.length) {
      // The PDP already renders a server-side empty state above the list;
      // only the reviews page needs a message here.
      listEl.innerHTML = document.querySelector('.rv2-empty')
        ? ''
        : '<p class="rv2-msg">No reviews yet.</p>';
      renderPagination(els, 1);
      return;
    }

    var sorted = sortReviews(allReviews, currentSort);
    var totalPages = Math.ceil(sorted.length / els.pageSize);
    if (currentPage > totalPages - 1) currentPage = 0;

    var start = currentPage * els.pageSize;
    var pageItems = sorted.slice(start, start + els.pageSize);

    listEl.innerHTML = pageItems.map(renderReviewCard).join('');
    renderPagination(els, totalPages);
  }

  var VERIFIED_BADGE =
    '<span class="rv2-review__verified">'
    + '<svg width="14" height="14" viewBox="0 0 256 256" aria-hidden="true"><path d="M225.86,102.82c-3.77-3.94-7.67-8-9.14-11.57-1.36-3.27-1.44-8.69-1.52-13.94-.15-9.76-.31-20.82-8-28.51s-18.75-7.85-28.51-8c-5.25-.08-10.67-.16-13.94-1.52-3.56-1.47-7.63-5.37-11.57-9.14C146.28,23.51,138.44,16,128,16s-18.27,7.51-25.18,14.14c-3.94,3.77-8,7.67-11.57,9.14C88,40.64,82.56,40.72,77.31,40.8c-9.76.15-20.82.31-28.51,8S41,67.55,40.8,77.31c-.08,5.25-.16,10.67-1.52,13.94-1.47,3.56-5.37,7.63-9.14,11.57C23.51,109.72,16,117.56,16,128s7.51,18.27,14.14,25.18c3.77,3.94,7.67,8,9.14,11.57,1.36,3.27,1.44,8.69,1.52,13.94.15,9.76.31,20.82,8,28.51s18.75,7.85,28.51,8c5.25.08,10.67.16,13.94,1.52,3.56,1.47,7.63,5.37,11.57,9.14C109.72,232.49,117.56,240,128,240s18.27-7.51,25.18-14.14c3.94-3.77,8-7.67,11.57-9.14,3.27-1.36,8.69-1.44,13.94-1.52,9.76-.15,20.82-.31,28.51-8s7.85-18.75,8-28.51c.08-5.25.16-10.67,1.52-13.94,1.47-3.56,5.37-7.63,9.14-11.57C232.49,146.28,240,138.44,240,128S232.49,109.73,225.86,102.82Zm-52.2,6.84-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z" fill="#3b82f6"/></svg>'
    + ' Verified</span>';

  var AVATAR_ICON =
    '<svg width="16" height="16" viewBox="0 0 256 256" aria-hidden="true"><path d="M230.92,212c-15.23-26.33-38.7-45.21-66.09-54.16a72,72,0,1,0-73.66,0C63.78,166.78,40.31,185.66,25.08,212a8,8,0,1,0,13.85,8C55.71,194.65,80.18,176,128,176s72.29,18.65,89.07,44a8,8,0,1,0,13.85-8ZM72,96a56,56,0,1,1,56,56A56.06,56.06,0,0,1,72,96Z" fill="#64748b"/></svg>';

  var VIDEO_EXTS = ['mp4', 'mov', 'webm', 'm4v', 'ogv'];

  /**
   * Review media can be an image or a video. A video URL never paints inside an
   * <img>, so it must be detected and rendered with a <video> element.
   * Mirrors snippets/includ-reviews-media-thumb-v2.liquid.
   * @param {string} url
   * @returns {boolean}
   */
  function isVideoUrl(url) {
    var clean = String(url || '').split('?')[0].toLowerCase();
    var ext = clean.split('.').pop();
    return VIDEO_EXTS.indexOf(ext) > -1 || clean.indexOf('/videos/') > -1;
  }

  /**
   * @param {string} url
   * @returns {string} `.rv2-thumb` markup for one media item
   */
  function mediaThumbHtml(url) {
    var safe = ReviewsV2.esc(url);

    if (isVideoUrl(url)) {
      // #t=0.1 makes the browser decode and paint one frame as a poster.
      return '<div class="rv2-thumb rv2-thumb--video">'
        + '<video src="' + safe + '#t=0.1" muted playsinline preload="metadata"'
        + ' width="72" height="72" aria-label="Review video" data-rv2-lightbox-video></video>'
        + '</div>';
    }

    return '<div class="rv2-thumb">'
      + '<img src="' + safe + '" alt="Review photo" loading="lazy" width="72" height="72" data-rv2-lightbox-img>'
      + '</div>';
  }

  function renderReviewCard(review) {
    var media = review.images || [];

    var html = '<div class="rv2-divider rv2-divider--dotted"></div>';
    html += '<div class="rv2-review">';
    html += '<div class="rv2-review__top">';
    html += '<div class="rv2-review__stars">' + ReviewsV2.starsHtml(review.rating || 0) + '</div>';
    if (review.verified) html += VERIFIED_BADGE;
    html += '</div>';

    if (review.title) {
      html += '<p class="rv2-review__title">' + ReviewsV2.esc(review.title) + '</p>';
    }
    if (review.body) {
      html += '<p class="rv2-review__body">' + ReviewsV2.esc(review.body) + '</p>';
    }

    if (media.length > 0) {
      html += '<div class="rv2-review__photos">';
      media.slice(0, 3).forEach(function(url) {
        html += mediaThumbHtml(url);
      });
      html += '</div>';
    }

    html += '<div class="rv2-review__footer">';
    html += '<div class="rv2-review__author">' + AVATAR_ICON;
    html += '<span>' + ReviewsV2.esc(review.author || 'Anonymous') + '</span>';
    html += '</div>';
    if (review.date) {
      html += '<span class="rv2-review__date">' + ReviewsV2.esc(review.date) + '</span>';
    }
    html += '</div>';
    html += '</div>';

    return html;
  }

  function renderPagination(els, totalPages) {
    var el = document.getElementById(els.pagination);
    if (!el) return;
    if (totalPages <= 1) { el.style.display = 'none'; return; }

    el.style.display = 'flex';
    var html = '';

    html += '<button class="rv2-pagination__btn" data-rv2-page-dir="prev" ' + (currentPage <= 0 ? 'disabled' : '') + '>';
    html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    html += '</button>';

    for (var i = 0; i < totalPages && i < 5; i++) {
      html += '<button class="rv2-pagination__num' + (i === currentPage ? ' is-active' : '') + '" data-rv2-page="' + i + '">' + (i + 1) + '</button>';
    }

    html += '<button class="rv2-pagination__btn" data-rv2-page-dir="next" ' + (currentPage >= totalPages - 1 ? 'disabled' : '') + '>';
    html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    html += '</button>';

    el.innerHTML = html;

    el.onclick = function(e) {
      var pageBtn = e.target.closest('[data-rv2-page]');
      var dirBtn = e.target.closest('[data-rv2-page-dir]');
      var newPage = null;

      if (pageBtn) {
        newPage = parseInt(pageBtn.dataset.rv2Page);
      } else if (dirBtn) {
        if (dirBtn.dataset.rv2PageDir === 'prev' && currentPage > 0) newPage = currentPage - 1;
        if (dirBtn.dataset.rv2PageDir === 'next' && currentPage < totalPages - 1) newPage = currentPage + 1;
      }

      if (newPage != null && newPage !== currentPage) {
        currentPage = newPage;
        render(els);
      }
    };
  }

  // ═════════════════════════════════════════════════════════════════════════
  // FORM — Write a Review
  // ═════════════════════════════════════════════════════════════════════════

  function initForm(productId, writeBtnId) {
    // Resolved on document: portalModalToBody() moved the overlay to <body>.
    var overlay = document.getElementById('rv2-form-overlay');
    var form = document.getElementById('rv2-form');
    var writeBtn = document.getElementById(writeBtnId);
    if (!overlay || !form) return;

    var backdrop = overlay.querySelector('.rv2-form-overlay__backdrop');
    var closeBtn = overlay.querySelector('.rv2-form-panel__close');
    var starsContainer = overlay.querySelector('#rv2-form-stars');
    var ratingInput = overlay.querySelector('#rv2-form-rating');
    var fileInput = overlay.querySelector('#rv2-form-files');
    var mediaRow = overlay.querySelector('#rv2-media-row');
    var submitBtn = overlay.querySelector('#rv2-form-submit');
    var statusEl = overlay.querySelector('#rv2-form-status');

    var selectedRating = 0;
    var uploadedFiles = [];
    var MAX_FILES = 5;

    // Open/Close
    var savedScrollY = 0;

    function openModal() {
      if (!ReviewsV2.isLoggedIn()) {
        window.location.href = '/customer_authentication/login?return_to=' + encodeURIComponent(window.location.pathname + window.location.search);
        return;
      }
      overlay.classList.add('is-open');

      var stickyBar = document.querySelector('sticky-add-to-cart');
      if (stickyBar) stickyBar.style.display = 'none';

      // Pin the body at its current offset — overflow:hidden alone is
      // unreliable on iOS Safari.
      savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      overlay.classList.remove('is-open');

      var stickyBar = document.querySelector('sticky-add-to-cart');
      if (stickyBar) stickyBar.style.display = '';

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';

      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, savedScrollY);
      document.documentElement.style.scrollBehavior = '';
    }

    if (writeBtn) writeBtn.addEventListener('click', openModal);
    if (backdrop) backdrop.addEventListener('click', closeModal);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeModal();
    });

    // Star rating
    if (starsContainer) {
      var starBtns = starsContainer.querySelectorAll('.rv2-form__star');
      starBtns.forEach(function(btn) {
        btn.addEventListener('click', function() {
          selectedRating = parseInt(btn.dataset.value, 10);
          ratingInput.value = selectedRating;
          updateStars(selectedRating);
        });
        btn.addEventListener('mouseenter', function() {
          updateStars(parseInt(btn.dataset.value, 10));
        });
      });
      starsContainer.addEventListener('mouseleave', function() {
        updateStars(selectedRating);
      });
    }

    function updateStars(n) {
      starsContainer.querySelectorAll('.rv2-form__star').forEach(function(b, i) {
        b.querySelector('path').setAttribute('fill', i < n ? '#eab308' : '#e2e8f0');
      });
    }

    // File upload
    if (fileInput) {
      fileInput.addEventListener('change', function(e) {
        var files = Array.from(e.target.files);
        var errEl = document.getElementById('rv2-err-media');
        if (errEl) errEl.textContent = '';

        files.forEach(function(file) {
          if (uploadedFiles.length >= MAX_FILES) {
            if (errEl) errEl.textContent = 'Maximum ' + MAX_FILES + ' files allowed';
            return;
          }
          if (file.size > 20 * 1024 * 1024) {
            if (errEl) errEl.textContent = 'Each file must be under 20MB';
            return;
          }
          uploadedFiles.push(file);
          addPreview(file);
        });
        fileInput.value = '';
      });
    }

    function addPreview(file) {
      var thumb = document.createElement('div');
      thumb.className = 'rv2-form__media-thumb';
      var objectUrl = URL.createObjectURL(file);

      if (file.type.indexOf('image/') === 0) {
        var img = document.createElement('img');
        img.src = objectUrl;
        img.alt = 'Upload preview';
        thumb.appendChild(img);
      } else if (file.type.indexOf('video/') === 0) {
        // #t=0.1 makes the browser decode and paint one frame as a poster.
        var video = document.createElement('video');
        video.src = objectUrl + '#t=0.1';
        video.muted = true;
        video.playsInline = true;
        video.preload = 'metadata';
        thumb.appendChild(video);
        thumb.classList.add('rv2-form__media-thumb--video');
      } else {
        thumb.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#64748b" stroke-width="1.5"/><path d="M10 8.5v7l5.5-3.5L10 8.5z" fill="#64748b"/></svg>';
      }

      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'rv2-form__media-remove';
      removeBtn.innerHTML = '&times;';
      removeBtn.setAttribute('aria-label', 'Remove ' + file.name);
      removeBtn.addEventListener('click', function() {
        var idx = uploadedFiles.indexOf(file);
        if (idx > -1) uploadedFiles.splice(idx, 1);
        URL.revokeObjectURL(objectUrl);
        thumb.remove();
      });
      thumb.appendChild(removeBtn);
      mediaRow.insertBefore(thumb, mediaRow.querySelector('.rv2-form__upload-btn'));
    }

    // Form submission
    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      if (!validate()) return;

      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';
      statusEl.textContent = '';
      statusEl.className = 'rv2-form__status';

      try {
        // Upload files in parallel if any
        var uploadResult = uploadedFiles.length > 0
          ? await ReviewsV2.uploadFiles(uploadedFiles)
          : { ok: true, gids: [], errors: [] };

        if (!uploadResult.ok) {
          var detail = (uploadResult.errors || []).join(' · ') || 'Media upload failed.';
          throw new Error(detail + ' Your review was not submitted — remove the file or try again.');
        }

        var mediaGids = uploadResult.gids || [];

        if (uploadedFiles.length > 0 && mediaGids.length !== uploadedFiles.length) {
          throw new Error('Only ' + mediaGids.length + ' of ' + uploadedFiles.length
            + ' files uploaded. Your review was not submitted — please try again.');
        }

        var result = await ReviewsV2.submitReview({
          shopifyProductId: productId,
          rating: selectedRating,
          title: document.getElementById('rv2-form-title').value.trim(),
          body: document.getElementById('rv2-form-body').value.trim(),
          authorDisplayName: document.getElementById('rv2-form-name').value.trim(),
          customerEmail: document.getElementById('rv2-form-email').value.trim(),
          mediaFileGids: mediaGids
        });

        if (result.ok) {
          statusEl.textContent = 'Success';
          resetForm();
          setTimeout(closeModal, 2500);
        } else {
          var msg = result.error ? result.error.message : 'Something went wrong.';
          statusEl.textContent = msg;
          statusEl.className = 'rv2-form__status rv2-form__status--error';
        }
      } catch (err) {
        statusEl.textContent = err.message || 'Something went wrong.';
        statusEl.className = 'rv2-form__status rv2-form__status--error';
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit Review';
      }
    });

    function validate() {
      var valid = true;
      clearErrors();

      if (!selectedRating) { showError('rv2-err-rating', 'Please select a rating'); valid = false; }

      var name = document.getElementById('rv2-form-name').value.trim();
      if (!name) { showError('rv2-err-name', 'Name is required'); valid = false; }

      // validate the format when the customer actually typed something.
      var email = document.getElementById('rv2-form-email').value.trim();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showError('rv2-err-email', 'Enter a valid email address');
        valid = false;
      }

      var body = document.getElementById('rv2-form-body').value.trim();
      if (!body || body.length < 10) { showError('rv2-err-body', 'Review must be at least 10 characters'); valid = false; }
      else if (body.length > 10000) { showError('rv2-err-body', 'Review must be under 10,000 characters'); valid = false; }

      return valid;
    }

    function showError(id, msg) {
      var el = document.getElementById(id);
      if (el) el.textContent = msg;
    }

    function clearErrors() {
      ['rv2-err-rating', 'rv2-err-name', 'rv2-err-email', 'rv2-err-body', 'rv2-err-media'].forEach(function(id) {
        var el = document.getElementById(id);
        if (el) el.textContent = '';
      });
    }

    function resetForm() {
      form.reset();
      selectedRating = 0;
      updateStars(0);
      uploadedFiles = [];
      mediaRow.querySelectorAll('.rv2-form__media-thumb').forEach(function(t) { t.remove(); });
    }
  }

  // ═════════════════════════════════════════════════════════════════════════
  // LIGHTBOX — review photos & videos
  // ═════════════════════════════════════════════════════════════════════════

  var LB_THUMB_SELECTOR = '[data-rv2-lightbox-img], [data-rv2-lightbox-video]';
  // A thumb belongs to the group it sits in: the collapsed strip, or one card.
  var LB_GROUP_SELECTOR = '.rv2-thumbs, .rv2-review__photos';

  var lb = null; // cached overlay refs, built on first open
  var lbItems = [];
  var lbIndex = 0;
  var lbLastFocus = null;

  /**
   * Thumbnails are the source of truth for the lightbox, so it works for the
   * Liquid-rendered strip, the JS-rendered cards and the reviews page alike —
   * no separate URL-to-review mapping to keep in sync.
   */
  function initLightbox() {
    document.addEventListener('click', function(e) {
      var thumb = e.target.closest(LB_THUMB_SELECTOR);
      if (!thumb) return;
      e.preventDefault();
      openLightbox(thumb);
    });
  }

  /** `#t=0.1` poster fragments must be dropped before reuse as a full source. */
  function lbSrcOf(node) {
    return (node.getAttribute('src') || node.currentSrc || '').split('#')[0];
  }

  function openLightbox(thumb) {
    var group = thumb.closest(LB_GROUP_SELECTOR);
    var nodes = group ? group.querySelectorAll(LB_THUMB_SELECTOR) : [thumb];

    lbItems = [];
    lbIndex = 0;
    Array.prototype.forEach.call(nodes, function(node) {
      var url = lbSrcOf(node);
      if (!url) return;
      if (node === thumb) lbIndex = lbItems.length;
      lbItems.push({ url: url, isVideo: node.tagName === 'VIDEO' });
    });
    if (!lbItems.length) return;

    if (!lb) lb = buildLightbox();

    lbLastFocus = document.activeElement;
    lb.overlay.classList.add('is-open');
    lb.overlay.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';

    // The PDP sticky bar paints above a fixed overlay on iOS.
    var stickyBar = document.querySelector('sticky-add-to-cart');
    if (stickyBar) stickyBar.style.display = 'none';

    renderLightbox();
    lb.closeBtn.focus();
  }

  function closeLightbox() {
    if (!lb) return;
    lb.overlay.classList.remove('is-open');
    lb.overlay.setAttribute('aria-hidden', 'true');
    lb.stage.innerHTML = ''; // stops any playing video
    document.body.style.overflow = '';

    var stickyBar = document.querySelector('sticky-add-to-cart');
    if (stickyBar) stickyBar.style.display = '';

    if (lbLastFocus && lbLastFocus.focus) lbLastFocus.focus();
    lbLastFocus = null;
  }

  function navLightbox(delta) {
    var next = lbIndex + delta;
    if (next < 0 || next > lbItems.length - 1) return;
    lbIndex = next;
    renderLightbox();
  }

  function renderLightbox() {
    var item = lbItems[lbIndex];
    lb.stage.innerHTML = '';

    if (item.isVideo) {
      var video = document.createElement('video');
      video.className = 'rv2-lb__media';
      video.src = item.url;
      video.controls = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      lb.stage.appendChild(video);
      // Opening is a user gesture, but autoplay can still be refused.
      var played = video.play();
      if (played && played.catch) played.catch(function() {});
    } else {
      var img = document.createElement('img');
      img.className = 'rv2-lb__media';
      img.src = item.url;
      img.alt = 'Review media ' + (lbIndex + 1) + ' of ' + lbItems.length;
      lb.stage.appendChild(img);
    }

    var multiple = lbItems.length > 1;
    lb.prevBtn.hidden = !multiple;
    lb.nextBtn.hidden = !multiple;
    lb.counter.hidden = !multiple;
    lb.prevBtn.disabled = lbIndex <= 0;
    lb.nextBtn.disabled = lbIndex >= lbItems.length - 1;
    lb.counter.textContent = (lbIndex + 1) + ' / ' + lbItems.length;
  }

  /** Built once, appended to <body> to escape ancestor stacking contexts. */
  function buildLightbox() {
    var overlay = document.createElement('div');
    overlay.className = 'rv2-lb';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Review media');
    overlay.setAttribute('aria-hidden', 'true');

    overlay.innerHTML =
      '<div class="rv2-lb__backdrop" data-rv2-lb-close></div>'
      + '<button class="rv2-lb__close" type="button" aria-label="Close">'
      + '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
      + '</button>'
      + '<button class="rv2-lb__nav rv2-lb__nav--prev" type="button" aria-label="Previous">'
      + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      + '</button>'
      + '<div class="rv2-lb__stage"></div>'
      + '<button class="rv2-lb__nav rv2-lb__nav--next" type="button" aria-label="Next">'
      + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      + '</button>'
      + '<span class="rv2-lb__counter" aria-live="polite"></span>';

    document.body.appendChild(overlay);

    var refs = {
      overlay: overlay,
      stage: overlay.querySelector('.rv2-lb__stage'),
      closeBtn: overlay.querySelector('.rv2-lb__close'),
      prevBtn: overlay.querySelector('.rv2-lb__nav--prev'),
      nextBtn: overlay.querySelector('.rv2-lb__nav--next'),
      counter: overlay.querySelector('.rv2-lb__counter')
    };

    refs.closeBtn.addEventListener('click', closeLightbox);
    refs.prevBtn.addEventListener('click', function() { navLightbox(-1); });
    refs.nextBtn.addEventListener('click', function() { navLightbox(1); });
    overlay.addEventListener('click', function(e) {
      if (e.target.closest('[data-rv2-lb-close]')) closeLightbox();
    });

    document.addEventListener('keydown', function(e) {
      if (!overlay.classList.contains('is-open')) return;
      if (e.key === 'Escape') { closeLightbox(); return; }
      if (e.key === 'ArrowLeft') { navLightbox(-1); return; }
      if (e.key === 'ArrowRight') { navLightbox(1); return; }
      if (e.key !== 'Tab') return;

      // Keep focus inside the dialog.
      var focusable = Array.prototype.filter.call(
        overlay.querySelectorAll('button:not([hidden]):not([disabled]), video[controls]'),
        function(el) { return el.offsetParent !== null; }
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    return refs;
  }

  // ═════════════════════════════════════════════════════════════════════════
  // BOOT
  // ═════════════════════════════════════════════════════════════════════════
  //
  // Must stay at the very bottom. `var` initialisers (allReviews, currentSort,
  // currentPage) run in source order, so calling init() earlier in the file
  // lets those initialisers execute afterwards and wipe whatever init() set.

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
